import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { ProfileService } from '../../services/profile.service';
import { CampaignFilters } from '../../services/campaign.service';
import {
  syncLocationSelections as syncLocationSelectionsUtil,
  getSelectedLocationLabels,
  subscribeToLocationsForFilterModal,
} from '../../shared/utils/filter-location.util';
import { ModalFooterActionsComponent } from '../../shared/components/modal-footer-actions/modal-footer-actions.component';

export interface CampaignFilterOptions {
  statuses: string[];
  campaignTypes: string[];
  locations: string[];
  sortBy: string;
}

export interface FilterItem {
  value: string;
  label: string;
  checked: boolean;
}

export interface SortOption {
  value: string;
  label: string;
  selected: boolean;
}

/**
 * Modal component for selecting and applying campaign filters (statuses, campaign types, locations, and sort).
 * Provides a UI to choose filter criteria, syncs with toolbar location selections and incoming active filter state,
 * and emits events to apply, clear, or close the modal. It also converts selections into an API-friendly format when applying.
 * Key methods: onApplyFilters, onClearFilters, onClose, onCancel, onLocationChange, onSortChange, hasActiveFilters.
 * Important lifecycle hooks:
 *   - ngOnInit: loads available locations for the filter modal.
 *   - ngOnChanges: syncs selections when inputs (isVisible, currentLocationLabels, activeFilters) change.
 *   - ngOnDestroy: unsubscribes from location updates to avoid memory leaks.
 * @component
 * @selector app-campaign-filter-modal
 * @example
 *   <app-campaign-filter-modal
 *     [isVisible]="isModalOpen"
 *     [currentLocationLabels]="toolbarLocationLabels"
 *     [activeFilters]="activeFilters"
 *     (applyFilters)="onApplyFilters($event)"
 *     (clearFilters)="onClearFilters()"
 *     (close)="onCloseModal()"
 *     (locationSelectionChanged)="onLocationSelectionChanged($event)">
 *   </app-campaign-filter-modal>
 * @input {boolean} isVisible - Controls modal visibility; when true the component may sync with provided activeFilters.
 * @input {string[]} currentLocationLabels - Current location labels from toolbar used to sync location selections.
 * @input {CampaignFilterOptions | null} activeFilters - Current active filter state to initialize the modal selections when opened.
 * @output {EventEmitter<void>} close - Emitted when the modal is requested to close (via close, cancel, or backdrop click).
 * @output {EventEmitter<CampaignFilterOptions>} applyFilters - Emitted with the selected filter options when the user applies filters.
 * @output {EventEmitter<void>} clearFilters - Emitted when the user clears all filter selections.
 * @output {EventEmitter<string[]>} locationSelectionChanged - Emitted with the currently selected location labels when a location selection toggles.
 */
@Component({
  selector: 'app-campaign-filter-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatRadioModule,
    ModalFooterActionsComponent,
  ],
  templateUrl: './campaign-filter-modal.component.html',
  styleUrl: './campaign-filter-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CampaignFilterModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isVisible = false;
  @Input() currentLocationLabels: string[] = []; // Current location selections from toolbar
  @Input() activeFilters: CampaignFilterOptions | null = null; // Current active filters
  @Output() close = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<CampaignFilterOptions>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() locationSelectionChanged = new EventEmitter<string[]>(); // Emit when location selection changes

  statuses: FilterItem[] = [
    { value: 'active', label: 'Active', checked: false },
    { value: 'scheduled', label: 'Scheduled', checked: false },
    { value: 'stopped', label: 'Stopped', checked: false },
    { value: 'completed', label: 'Completed', checked: false },
    { value: 'draft', label: 'Draft', checked: false },
    { value: 'Paused', label: 'Paused', checked: false },
    { value: 'resumed', label: 'Resumed', checked: false },
    { value: 'archived', label: 'Archived', checked: false },
  ];

  campaignTypes: FilterItem[] = [
    { value: 'email', label: 'Email', checked: false },
    { value: 'sms', label: 'SMS', checked: false },
    { value: 'facebook', label: 'Facebook', checked: false },
    { value: 'google', label: 'Google', checked: false },
  ];

  locations: FilterItem[] = [];

  sortOptions: SortOption[] = [
    { value: 'name-asc', label: 'Name in ascending order', selected: false },
    { value: 'name-desc', label: 'Name in descending order', selected: false },
  ];

  private locationsSubscription: any;

  constructor(private profileService: ProfileService) {}

  ngOnInit(): void {
    this.loadLocations();
  }

  ngOnDestroy(): void {
    if (this.locationsSubscription) {
      this.locationsSubscription.unsubscribe();
    }
  }

  /**
   * Handle Angular input changes for currentLocationLabels and isVisible, syncing selections and active filters
   * @example
   * gOnChanges({ currentLocationLabels: { firstChange: false, currentValue: {...} }, isVisible: { currentValue: true } })
   * void
   * @param {SimpleChanges} changes - Angular SimpleChanges object containing changed input properties.
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to currentLocationLabels input
    if (changes['currentLocationLabels'] && !changes['currentLocationLabels'].firstChange) {
      this.syncLocationSelections();
    }

    // When modal becomes visible, sync with active filters
    if (changes['isVisible'] && changes['isVisible'].currentValue === true && this.activeFilters) {
      this.syncWithActiveFilters();
    }
  }

  /**
   * Synchronize the component's filter option checked/selected states with the current activeFilters object.
   * @example
   * this.syncWithActiveFilters()
   * undefined
   * @param {{void}} none - No arguments are accepted.
   * @returns {{void}} Does not return a value; updates component state in-place.
   **/
  private syncWithActiveFilters(): void {
    if (!this.activeFilters) {
      return;
    }

    // Sync statuses
    this.statuses.forEach(status => {
      status.checked = this.activeFilters!.statuses?.includes(status.value) || false;
    });

    // Sync campaign types
    this.campaignTypes.forEach(type => {
      type.checked = this.activeFilters!.campaignTypes?.includes(type.value) || false;
    });

    // Sync locations
    this.locations.forEach(location => {
      location.checked = this.activeFilters!.locations?.includes(location.value) || false;
    });

    // Sync sort options
    const activeSortBy = this.activeFilters!.sortBy || '';
    this.sortOptions.forEach(option => {
      option.selected = option.value === activeSortBy;
    });
  }

  private loadLocations(): void {
    this.locationsSubscription = subscribeToLocationsForFilterModal(this.profileService, this);
  }

  private syncLocationSelections(): void {
    syncLocationSelectionsUtil(this.locations, this.currentLocationLabels);
  }

  onLocationChange(location: FilterItem): void {
    location.checked = !location.checked;
    this.locationSelectionChanged.emit(getSelectedLocationLabels(this.locations));
  }

  // Method to handle sort option changes
  onSortChange(selectedSort: SortOption): void {
    // Unselect all other options
    this.sortOptions.forEach(option => {
      option.selected = option.value === selectedSort.value;
    });
  }

  onClose(): void {
    this.close.emit();
  }

  /**
   * Collects selected statuses, campaign types, and locations from component state, converts them into comma-separated API filter fields and emits an applyFilters event with the original filter options for backward compatibility.
   * @example
   * nApplyFilters()
   * void
   * @returns {{void}} No value; emits an applyFilters event containing the selected filter options (statuses, campaignTypes, locations, and sortBy).
   **/
  onApplyFilters(): void {
    const selectedStatuses = this.statuses.filter(item => item.checked).map(item => item.value);
    const selectedTypes = this.campaignTypes.filter(item => item.checked).map(item => item.value);
    const selectedLocations = this.locations.filter(item => item.checked).map(item => item.value);

    // Convert to API filter format with comma-separated values
    const apiFilters: CampaignFilters = {};

    // Map campaign types to platform parameter (comma-separated)
    if (selectedTypes.length > 0) {
      apiFilters.platform = selectedTypes.join(',');
    }

    // Map statuses to campaign_status parameter (comma-separated)
    if (selectedStatuses.length > 0) {
      apiFilters.campaign_status = selectedStatuses.join(',');
    }

    // Map locations to location_id parameter (comma-separated)
    if (selectedLocations.length > 0) {
      apiFilters.location_id = selectedLocations.join(',');
    }

    // Emit both the original format for backward compatibility and the API format
    const filters: CampaignFilterOptions = {
      statuses: selectedStatuses,
      campaignTypes: selectedTypes,
      locations: selectedLocations,
      sortBy: this.sortOptions.find(option => option.selected)?.value || '',
    };

    this.applyFilters.emit(filters);
  }

  onClearFilters(): void {
    this.statuses.forEach(item => (item.checked = false));
    this.campaignTypes.forEach(item => (item.checked = false));
    this.locations.forEach(item => (item.checked = false));
    this.sortOptions.forEach(option => (option.selected = false));
    this.clearFilters.emit();
  }

  onCancel(): void {
    this.close.emit();
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  hasActiveFilters(): boolean {
    return (
      this.statuses.some(item => item.checked) ||
      this.campaignTypes.some(item => item.checked) ||
      this.locations.some(item => item.checked) ||
      this.sortOptions.some(option => option.selected)
    );
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
